import React, { Fragment, useEffect, useRef, useState } from 'react'
import { LazyImageFull, ImageState } from 'react-lazy-images'
import { graphql, useStaticQuery } from 'gatsby'
import {
  Head,
  ValuesTabs,
  Slider,
  Modal,
  AnimatedWelcome,
  AnimatedVideo,
} from '@components'
//import api from '@api/blog'
import { Link } from 'gatsby'
import * as st from '@assets/styl/home.module.styl'
import * as st2 from '@assets/styl/heroSlider.module.styl'
import { useLanguage } from '../hooks'
import { translatePath } from '../translations'
import cn from 'classnames'

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'

const Home = () => {
  const data = useStaticQuery(graphql`
    fragment ReleaseImage on File {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }

    query {
      allBlogPost {
        nodes {
          link
          title {
            rendered
          }
          _embedded {
            wp_featuredmedia {
              media_details {
                sizes {
                  post_thumb_big {
                    source_url
                  }
                }
              }
            }
          }
        }
      }
      allNumbers {
        nodes {
          name
          number
          title
        }
      }
      allShowcases {
        nodes {
          id
          image
          imageMobile
          title
          url
          video
          videoMobile
          imageFile {
            childImageSharp {
              gatsbyImageData(width: 1920, height: 1080, placeholder: BLURRED)
              resize(width: 1920, toFormat: WEBP, quality: 75) {
                src
              }
            }
          }
          imageMobileFile {
            childImageSharp {
              gatsbyImageData(width: 600, height: 1294, placeholder: BLURRED)
            }
          }
        }
      }
      allReleases {
        nodes {
          facade
          facadeMobile
          facadeHover
          facadeHome
          facadeHomeMobile
          facadeHomeHover
          facadeFile {
            ...ReleaseImage
          }
          facadeMobileFile {
            ...ReleaseImage
          }
          facadeHoverFile {
            ...ReleaseImage
          }
          facadeHomeFile {
            ...ReleaseImage
          }
          facadeHomeMobileFile {
            ...ReleaseImage
          }
          facadeHomeHoverFile {
            ...ReleaseImage
          }
          name
          neighborhood
          city
          tag
          url
          feature
        }
      }
      allPosts {
        nodes {
          title
          site
          url
        }
      }
    }
  `)
  const numbers = data.allNumbers.nodes
  const showcases = data.allShowcases.nodes
  const releases = data.allReleases.nodes
  const posts = data.allPosts.nodes
  const blogPosts = data.allBlogPost.nodes
  const [isMobile, setIsMobile] = useState(false)
  const sectionNumbers = useRef(null)
  const heroRef = useRef<HTMLElement>(null)

  const { t } = useLanguage()

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger)
    const numbers = gsap.utils.toArray('.numbers') as any

    const animateNumber = (
      element: HTMLElement,
      endValue: number,
      symbols: string[],
      index: number
    ) => {
      const obj = { value: 0 }

      gsap.to(obj, {
        value: endValue,
        duration: 2,
        onUpdate: () => {
          element.innerHTML = `${symbols[index]}${Math.floor(
            obj.value
          ).toLocaleString('de-DE')}`
        },
      })
    }

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionNumbers.current,
        start: '20% center',
        end: 'bottom center',
      },
    })

    numbers.forEach((numberElement: any, index: number) => {
      const formattedValue = numberElement.dataset.value
        .toString()
        .trim()
        .replace(/[^0-9,.]/g, '')

      const numericValue = parseFloat(
        formattedValue.replace(/\./g, '').replace(',', '.')
      )

      const symbols = ['+', '+', '+', '+']

      tl.to(
        numberElement,
        {
          opacity: 1,
          delay: 0.1 * index,
          onStart: () =>
            animateNumber(numberElement, numericValue, symbols, index),
        },
        '<'
      )
    })
  }, [])

  useEffect(() => {
    const playVideos = () => {
      heroRef.current?.querySelectorAll('video').forEach((video) => {
        video.play().catch(() => {})
      })
    }
    if (document.readyState === 'complete') {
      playVideos()
    } else {
      window.addEventListener('load', playVideos)
      return () => window.removeEventListener('load', playVideos)
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1199)
    }
    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [numbers])

  return (
    <>
      <Head pathname="/" />

      <section className={cn(st.hero, 'heroScroll')} ref={heroRef}>
        <Slider
          classes={{ root: st2.slidesWrapper, arrow: st2.arrow }}
          slidesToShow={1}
          responsiveSize={1199}
          isArrow={showcases.length > 1 ? true : false}
          finite
        >
          {showcases.map((item) => {
            const poster =
              item.imageFile?.childImageSharp?.resize?.src || item.image
            const image = getImage(item.imageFile)
            const imageMobile = getImage(item.imageMobileFile)

            return (
              <div className={st2.alice} key={item.title}>
                <a
                  href={item.url !== ' ' ? item.url : 'javascript:;'}
                  target={item.url !== ' ' ? '_blank' : ''}
                >
                  <div className={st2.slide}>
                    {item.video ? (
                      <div className={st2.item}>
                        <video
                          className={st2.media}
                          poster={poster}
                          muted
                          loop
                          playsInline
                          preload="none"
                        >
                          <source src={item.video} type="video/mp4" />
                        </video>
                      </div>
                    ) : (
                      <div className={st2.item}>
                        {image ? (
                          <GatsbyImage
                            image={image}
                            alt={item.title}
                            className={st2.desktop}
                          />
                        ) : (
                          <img
                            src={item.image}
                            alt={item.title}
                            className={st2.desktop}
                            loading="lazy"
                          />
                        )}
                        {imageMobile ? (
                          <GatsbyImage
                            image={imageMobile}
                            alt={item.title}
                            className={st2.mobile}
                          />
                        ) : (
                          <img
                            src={item.imageMobile}
                            alt={item.title}
                            className={st2.mobile}
                            loading="lazy"
                          />
                        )}
                      </div>
                    )}
                    <div className={st2.grid}>
                      <span className={st2.slideText}>{item.title}</span>
                    </div>
                  </div>
                </a>
              </div>
            )
          })}
        </Slider>
      </section>

      <AnimatedWelcome />

      <AnimatedVideo />

      <section className={cn(st.releases, st.releasesEnterprises)}>
        <div className={st.releasesContainer}>
          <h2>{t('Home.releases')}</h2>
          <p>
            {t('Home.releasesText')
              .split('\n')
              .map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < t('Home.releasesText').split('\n').length - 1 && (
                    <br />
                  )}
                </React.Fragment>
              ))}
          </p>

          {isMobile && (
            <Link
              to={translatePath('/empreendimentos/lancamentos/')}
              className="btn spacing-mobile"
            >
              {t('Home.moreEnterprises')}
            </Link>
          )}

          <ul>
            {releases.map((release, index) => {
              const isFirst = index === 0

              // File nodes otimizados (conforme index)
              const desktopFile = isFirst
                ? release.facadeFile
                : release.facadeHomeFile
              const mobileFile = isFirst
                ? release.facadeMobileFile
                : release.facadeHomeMobileFile
              const hoverFile = isFirst
                ? release.facadeHoverFile
                : release.facadeHomeHoverFile

              // URLs originais (fallback), espelhando a lógica acima
              const desktopUrl = isFirst ? release.facade : release.facadeHome
              const mobileUrl = isFirst
                ? release.facadeMobile
                : release.facadeHomeMobile
              const hoverUrl = isFirst
                ? release.facadeHover
                : release.facadeHomeHover

              // escolha mobile/desktop em runtime, como no original
              const mainFile = isMobile ? mobileFile : desktopFile
              const mainUrl = isMobile ? mobileUrl : desktopUrl

              const hoverFinalFile = isMobile ? mobileFile : hoverFile
              const hoverFinalUrl = isMobile ? mobileUrl : hoverUrl

              const hasHover = release.facadeHomeHover || release.facadeHover

              return (
                <li key={index} className={isFirst ? '' : st.smallerList}>
                  <div>
                    <div className={st.imageWrapper}>
                      {/* Imagem principal */}
                      <OptimizedImage
                        file={mainFile}
                        fallbackSrc={mainUrl}
                        alt=""
                        className={isFirst ? '' : st.smallerImage}
                      />

                      {/* Imagem de hover */}
                      {hasHover ? (
                        <OptimizedImage
                          file={hoverFinalFile}
                          fallbackSrc={hoverFinalUrl}
                          alt=""
                          className={cn(
                            st.animationHover,
                            isFirst ? '' : st.smallerImage
                          )}
                        />
                      ) : null}
                    </div>

                    <div>
                      <h4>{release.name}</h4>
                      <p>
                        {release.neighborhood} / {release.city}
                      </p>
                    </div>
                  </div>
                  <a
                    href={release.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={release.name}
                  ></a>
                </li>
              )
            })}
          </ul>

          {!isMobile && (
            <Link
              to={translatePath('/empreendimentos/lancamentos/')}
              className="btn spacing"
            >
              {t('Home.moreEnterprises')}
            </Link>
          )}
        </div>
      </section>

      <section className={st.numbersContainer} ref={sectionNumbers}>
        <div>
          <h2>
            {t('Home.numbers')
              .split('\n')
              .map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < t('Home.numbers').split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
          </h2>
          <p>
            {t('Home.numbersText')
              .split('\n')
              .map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < t('Home.numbersText').split('\n').length - 1 && (
                    <br />
                  )}
                </React.Fragment>
              ))}
          </p>
          <Link
            to={translatePath('/empreendimentos/entregues/')}
            className={cn('btn-white', isMobile ? 'spacing-mobile' : 'spacing')}
          >
            {t('Home.discoverOurPortfolio')}
          </Link>
          <ul className={st.numbers}>
            {numbers.map((number, key) => (
              <li key={key}>
                <div>
                  <div data-value={number.number} className="numbers">
                    0
                  </div>
                  {number.title}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {Boolean(posts && posts.length) && (
        <section className={st.blog}>
          <div>
            <h2>{t('Home.intheMedia')}</h2>
            <Slider
              classes={{ root: st.slidesWrapper, arrow: st.arrow }}
              dots
              slidesToShow={3}
              responsiveSize={1199}
            >
              {posts.map((post, key) => (
                <a
                  href={post.url}
                  target="_blank"
                  key={key}
                  className={st.slide}
                  itemScope
                  itemType="http://schema.org/BlogPosting"
                >
                  <meta itemProp="url" content={post.url} />
                  <span itemProp="publisher">{post.site}</span>
                  <h4 itemProp="name">{post.title}</h4>
                </a>
              ))}
            </Slider>
          </div>
        </section>
      )}

      <section className={st.blogDimasContainer}>
        <h2>Blog Dimas</h2>
        <div>
          {blogPosts.map((item, index) => {
            const image =
              item._embedded['wp_featuredmedia'][0].media_details.sizes
                .post_thumb_big.source_url || ''
            return (
              <div key={index} className={st.blogDimas}>
                <figure>
                  <img
                    src={image}
                    alt={item.title.rendered}
                    width={543}
                    height={335}
                  />
                </figure>
                <h3>{item.title.rendered}</h3>
                <a
                  href={item.link}
                  target="_blank"
                  title=""
                  className={st.effect}
                ></a>
              </div>
            )
          })}
        </div>
        <Link
          to={translatePath('https://dimasconstrucoes.com.br/blog/')}
          target="_blank"
          className="btn"
        >
          {t('Home.blogDimasLink')}
        </Link>
      </section>
    </>
  )
}

const OptimizedImage = ({ file, fallbackSrc, ...props }) => {
  const image = getImage(file)
  return image ? (
    <GatsbyImage image={image} alt="" {...props} />
  ) : (
    <img src={fallbackSrc} loading="lazy" {...props} />
  )
}

export default Home
